import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Award,
  CheckCircle2,
  XCircle,
  Lock,
  Unlock,
  ChevronRight,
  RotateCcw,
  BookOpen,
} from "lucide-react";
import { toast } from "sonner";
import { Legacy360Plan125CertGate } from "@/components/Legacy360Plan125CertGate";

const VERTICAL_CONFIG: Record<string, { color: string; bgColor: string; borderColor: string; description: string; landingPage: string; dotColor: string }> = {
  Fundable360: {
    color: "text-green-700",
    bgColor: "bg-green-50",
    borderColor: "border-green-200",
    dotColor: "#7FA63E",
    description: "Business credit building & funding access",
    landingPage: "https://fundable360.wibiz.ai/",
  },
  Share360: {
    color: "text-teal-700",
    bgColor: "bg-teal-50",
    borderColor: "border-teal-200",
    dotColor: "#159189",
    description: "Charitable giving with donor benefits",
    landingPage: "https://share360.wibiz.ai/",
  },
  Legacy360: {
    color: "text-amber-700",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200",
    dotColor: "#BE8A2C",
    description: "Estate planning & wealth preservation",
    landingPage: "https://legacy360.wibiz.ai/",
  },
  Scale360: {
    color: "text-orange-700",
    bgColor: "bg-orange-50",
    borderColor: "border-orange-200",
    dotColor: "#E2701F",
    description: "AI automation, employee replacement, efficiency",
    landingPage: "https://scale360.wibiz.ai/",
  },
};

const CERT_VERTICALS = ["Fundable360", "Share360", "Legacy360", "Scale360"] as const;
type CertVertical = (typeof CERT_VERTICALS)[number];

type QuizState = "idle" | "taking" | "result";

interface Props {
  repCode: string;
  ghlContactId?: string | null;
  repFirstName?: string | null;
  repLastName?: string | null;
  repEmail?: string | null;
}

export function CertificationQuizTab({ repCode, ghlContactId, repFirstName, repLastName, repEmail }: Props) {
  const [activeVertical, setActiveVertical] = useState<CertVertical | null>(null);
  const [quizState, setQuizState] = useState<QuizState>("idle");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<any>(null);
  // Kickstart provisioning state (Scale360 only)
  const [kickstartSent, setKickstartSent] = useState(false);
  const [kickstartLoading, setKickstartLoading] = useState(false);
  // Legacy360 125 Plan uses its own separate cert system (two videos + a
  // dedicated 10-question quiz, 8/10 to pass) rather than the generic
  // certification.* router the four verticals above use — see
  // Legacy360Plan125CertGate.tsx. This just toggles which view is shown;
  // it does not read or write any certification data itself.
  const [showLegacy360Plan125, setShowLegacy360Plan125] = useState(false);

  const { data: certifications = [], refetch: refetchCerts } = trpc.certification.getByRepCode.useQuery(
    { repCode },
    { enabled: !!repCode }
  );

  const plan125StatusQuery = trpc.legacy360Plan125.getStatus.useQuery(
    { repCode },
    { enabled: !!repCode }
  );

  const { data: quizData, isLoading: quizLoading } = trpc.certification.getQuiz.useQuery(
    { vertical: activeVertical! },
    { enabled: !!activeVertical && quizState === "taking" }
  );

  const submitQuiz = trpc.certification.submitQuiz.useMutation({
    onSuccess: (data) => {
      setResult(data);
      setQuizState("result");
      refetchCerts();
      if (data.passed) {
        toast.success(`🎉 Congratulations! You passed the ${activeVertical} certification!`);
      } else {
        toast.error(`You scored ${data.score}/${data.total}. You need ${data.passingScore} to pass. Try again!`);
      }
    },
    onError: (e) => toast.error(e.message),
  });

  // Get latest cert per vertical
  const latestByVertical: Record<string, any> = {};
  certifications.forEach((c: any) => {
    if (!latestByVertical[c.vertical] || c.attemptNumber > latestByVertical[c.vertical].attemptNumber) {
      latestByVertical[c.vertical] = c;
    }
  });

  const certifiedVerticals = CERT_VERTICALS.filter(v => latestByVertical[v]?.passed);

  function startQuiz(vertical: CertVertical) {
    setActiveVertical(vertical);
    setAnswers({});
    setResult(null);
    setQuizState("taking");
  }

  async function activateKickstart() {
    setKickstartLoading(true);
    try {
      const res = await fetch("/api/scale360/kickstart/provision", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ repCode }),
      });
      const data = await res.json();
      setKickstartSent(true);
      if (data.skipped) {
        toast.info("Kickstart is not yet available. We'll notify you when it launches.");
      } else if (data.sent) {
        toast.success("Kickstart activation request sent!");
      } else {
        toast.info("Kickstart activation noted. We'll follow up soon.");
      }
    } catch {
      setKickstartSent(true);
      toast.info("Kickstart is not yet available — your certification is saved.");
    } finally {
      setKickstartLoading(false);
    }
  }

  function handleSubmit() {
    if (!activeVertical || !quizData) return;
    const unanswered = quizData.questions.filter((q: any) => !answers[q.id]);
    if (unanswered.length > 0) {
      toast.error(`Please answer all ${quizData.questions.length} questions before submitting.`);
      return;
    }
    submitQuiz.mutate({
      repCode,
      vertical: activeVertical,
      answers,
      ghlContactId: ghlContactId ?? undefined,
    });
  }

  // ── Legacy360 125 Plan Screen ────────────────────────────────────────────
  // Renders the existing two-video + quiz gate unchanged — this tab only
  // decides when to show it, it doesn't reimplement any of its behavior.
  if (showLegacy360Plan125) {
    return (
      <div className="max-w-2xl mx-auto py-6 px-4">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => setShowLegacy360Plan125(false)} className="text-gray-400 hover:text-gray-600 transition-colors text-sm">
            ← Back
          </button>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Legacy360 125 Plan Certification</h2>
            <p className="text-xs text-gray-500">Watch both training videos, then pass the 10-question quiz (8/10 required)</p>
          </div>
        </div>
        <Legacy360Plan125CertGate repCode={repCode} />
      </div>
    );
  }

  // ── Result Screen ──────────────────────────────────────────────────────────
  if (quizState === "result" && result) {
    const isScale360Pass = activeVertical === "Scale360" && result.passed;
    return (
      <div className="max-w-2xl mx-auto py-8 px-4">
        <div className={`rounded-2xl border-2 p-8 text-center ${result.passed ? "border-green-300 bg-green-50" : "border-red-200 bg-red-50"}`}>
          {result.passed ? (
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          ) : (
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          )}

          {/* Scale360-specific pass title */}
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {isScale360Pass
              ? "Scale360 Certification Passed"
              : result.passed ? "Certification Passed!" : "Not Quite There Yet"}
          </h2>

          {isScale360Pass ? (
            <p className="text-gray-600 mb-1">You can now sell Scale360 in BC360</p>
          ) : (
            <p className="text-gray-600 mb-1">
              You scored <strong>{result.score}</strong> out of <strong>{result.total}</strong> ({Math.round((result.score / result.total) * 100)}%)
            </p>
          )}
          <p className="text-sm text-gray-500 mb-6">
            {isScale360Pass
              ? "Kickstart is optional — continue to WiBiz Certification if you'd like to unlock the full Scale360 platform."
              : `Passing score: ${result.passingScore}/${result.total} (${Math.round((result.passingScore / result.total) * 100)}%)`}
          </p>

          {/* Generic unlocked items — shown for all verticals on pass */}
          {result.passed && (
            <div className="bg-white rounded-xl border border-green-200 p-4 mb-6 text-left">
              <p className="text-sm font-semibold text-green-700 mb-2">🔓 You've unlocked:</p>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>✅ {activeVertical} Leads, Pipeline & Clients</li>
                <li>✅ Your personalised {activeVertical} referral link</li>
              </ul>
            </div>
          )}

          {/* Share360-specific post-certification message */}
          {activeVertical === "Share360" && result.passed && (
            <div className="bg-[#1A6FD4]/5 border border-[#1A6FD4]/20 rounded-xl p-5 mb-6 text-left">
              <p className="text-sm font-semibold text-[#1A6FD4] mb-1">Share360 Certification Complete!</p>
              <p className="text-xs text-gray-500">
                Your certification has been recorded. The BC360 admin team will review your enrollment and activate your Share360 Retail Associate access. You'll be notified once your account is approved.
              </p>
            </div>
          )}

          {/* Scale360-specific Kickstart CTA */}
          {isScale360Pass && (
            <div className="bg-[#E8620A]/5 border border-[#E8620A]/20 rounded-xl p-5 mb-6 text-left">
              <p className="text-sm font-semibold text-[#E8620A] mb-1">Next step: Kickstart (optional)</p>
              <p className="text-xs text-gray-500 mb-4">
                Kickstart is the full Scale360 platform. Activate it now or come back later — your BC360 access is already active.
              </p>
              {kickstartSent ? (
                <div className="flex items-center gap-2 text-sm text-green-700 font-medium">
                  <CheckCircle2 className="w-4 h-4" /> Request submitted — we'll follow up soon.
                </div>
              ) : (
                <div className="flex gap-3 flex-wrap">
                  <Button
                    onClick={activateKickstart}
                    disabled={kickstartLoading}
                    className="bg-[#E8620A] hover:bg-[#c9530a] text-white border-0 text-sm"
                  >
                    {kickstartLoading ? "Sending…" : "Activate Kickstart Access"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setKickstartSent(true)}
                    className="text-sm text-gray-500 border-gray-200"
                  >
                    Maybe Later
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Answer Review */}
          <div className="text-left mb-6">
            <h3 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wide">Answer Review</h3>
            <div className="space-y-2">
              {result.gradedAnswers.map((a: any, i: number) => (
                <div key={a.questionId} className={`flex items-start gap-2 p-2 rounded-lg text-sm ${a.isCorrect ? "bg-green-50" : "bg-red-50"}`}>
                  {a.isCorrect ? (
                    <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                  )}
                  <span className="text-gray-700">Q{i + 1}: {a.isCorrect ? "Correct" : `Incorrect — you answered: "${a.selectedAnswer}"`}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => { setQuizState("idle"); setActiveVertical(null); }}>
              Back to Certifications
            </Button>
            {!result.passed && (
              <Button onClick={() => startQuiz(activeVertical!)} className="gap-1.5 bg-[#84ab4a] hover:bg-[#6e9038] text-white border-0">
                <RotateCcw className="w-4 h-4" /> Retake Quiz
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Quiz Screen ────────────────────────────────────────────────────────────
  if (quizState === "taking" && activeVertical) {
    const conf = VERTICAL_CONFIG[activeVertical];
    return (
      <div className="max-w-2xl mx-auto py-6 px-4">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => setQuizState("idle")} className="text-gray-400 hover:text-gray-600 transition-colors text-sm">
            ← Back
          </button>
          <div>
            <h2 className="text-lg font-bold text-gray-900">{activeVertical} Certification Quiz</h2>
            <p className="text-xs text-gray-500">Answer {quizData?.passingScore ?? "—"} out of {quizData?.totalQuestions ?? "—"} questions correctly to pass</p>
          </div>
        </div>

        {quizLoading ? (
          <div className="text-center py-12 text-gray-400">Loading quiz...</div>
        ) : quizData ? (
          <div className="space-y-6">
            {quizData.questions.map((q: any, idx: number) => (
              <div key={q.id} className="bg-white border border-gray-200 rounded-xl p-5">
                <p className="font-semibold text-gray-800 mb-4">
                  <span className="text-gray-400 mr-2">Q{idx + 1}.</span>
                  {q.question}
                </p>
                <div className="space-y-2">
                  {q.options.map((opt: string) => (
                    <label
                      key={opt}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        answers[q.id] === opt
                          ? `${conf.bgColor} ${conf.borderColor} border-2`
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <input
                        type="radio"
                        name={q.id}
                        value={opt}
                        checked={answers[q.id] === opt}
                        onChange={() => setAnswers(prev => ({ ...prev, [q.id]: opt }))}
                        className="mt-0.5 shrink-0"
                      />
                      <span className={`text-sm ${answers[q.id] === opt ? conf.color : "text-gray-700"}`}>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex items-center justify-between pt-2">
              <p className="text-sm text-gray-500">
                {Object.keys(answers).length}/{quizData.questions.length} answered
              </p>
              <Button
                onClick={handleSubmit}
                disabled={submitQuiz.isPending || Object.keys(answers).length < quizData.questions.length}
                className="bg-[#84ab4a] hover:bg-[#6e9038] text-white border-0 px-8"
              >
                {submitQuiz.isPending ? "Submitting..." : "Submit Quiz"}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  // ── Idle Screen — Certification Overview ──────────────────────────────────
  const inProgress  = CERT_VERTICALS.filter(v => latestByVertical[v] && !latestByVertical[v].passed);
  const notStarted  = CERT_VERTICALS.filter(v => !latestByVertical[v]);

  return (
    <div className="space-y-7">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Earned — dark card */}
        <div className="rounded-2xl p-6 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #2E2722 0%, #3A2F25 60%, #46342A 100%)" }}>
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 80% -20%, rgba(127,166,62,0.30) 0%, transparent 60%)" }} />
          <div className="relative">
            <div className="text-[10px] text-white/50 uppercase tracking-widest font-semibold mb-2">Certifications Earned</div>
            <div className="text-4xl font-bold text-white mb-2" style={{ fontFamily: "'Spectral', serif" }}>
              {certifiedVerticals.length} <span className="text-white/40 text-2xl">/ {CERT_VERTICALS.length}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-[#7FA63E]" />
              <span className="text-xs text-white/60">All verticals available to certify</span>
            </div>
          </div>
        </div>
        {/* In Progress */}
        <div className="bg-white border border-[#EBE2D4] rounded-2xl p-6 shadow-sm">
          <div className="text-[10px] text-[#9C9285] uppercase tracking-widest font-semibold mb-2">In Progress</div>
          <div className="text-4xl font-bold text-[#2C2723] mb-2" style={{ fontFamily: "'Spectral', serif" }}>{inProgress.length}</div>
          {inProgress.length > 0 ? (
            <div className="space-y-0.5">
              {inProgress.map(v => {
                const l = latestByVertical[v];
                const pct = l ? Math.round((l.score / l.totalQuestions) * 100) : 0;
                return <div key={v} className="text-xs text-[#E2701F] font-medium">{v} · {pct}%</div>;
              })}
            </div>
          ) : (
            <div className="text-xs text-[#9C9285]">No certifications in progress</div>
          )}
        </div>
        {/* Available to Start */}
        <div className="bg-white border border-[#EBE2D4] rounded-2xl p-6 shadow-sm">
          <div className="text-[10px] text-[#9C9285] uppercase tracking-widest font-semibold mb-2">Available to Start</div>
          <div className="text-4xl font-bold text-[#2C2723] mb-2" style={{ fontFamily: "'Spectral', serif" }}>{notStarted.length}</div>
          {notStarted.length > 0 ? (
            <div className="space-y-0.5">
              {notStarted.map(v => <div key={v} className="text-xs text-[#6E665A]">{v}</div>)}
            </div>
          ) : (
            <div className="text-xs text-[#9C9285]">All certifications started</div>
          )}
        </div>
      </div>

      {/* Scale360 Kickstart CTA */}
      {certifiedVerticals.includes("Scale360") && (
        <div className="bg-[#FBEEE1] border border-[#E2701F]/25 rounded-2xl p-5 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-[#C75E12]">Scale360 Kickstart Access (optional)</p>
            <p className="text-xs text-[#9C9285] mt-0.5">Activate the full Scale360 platform and get your portal credentials.</p>
          </div>
          {kickstartSent ? (
            <div className="flex items-center gap-1.5 text-xs text-[#5F8A2E] font-semibold flex-shrink-0"><CheckCircle2 className="w-4 h-4" /> Submitted</div>
          ) : (
            <Button onClick={activateKickstart} disabled={kickstartLoading} className="bg-[#E2701F] hover:bg-[#C75E12] text-white border-0 text-xs flex-shrink-0 whitespace-nowrap">
              {kickstartLoading ? "Sending…" : "Activate Kickstart"}
            </Button>
          )}
        </div>
      )}

      {/* Your Certifications list */}
      <div>
        <div className="mb-4">
          <h2 className="text-lg font-bold text-[#2C2723]" style={{ fontFamily: "'Spectral', serif" }}>Your Certifications</h2>
          <p className="text-sm text-[#9C9285] mt-0.5">Complete a vertical's certification to unlock its tools</p>
        </div>
        <div className="bg-white border border-[#EBE2D4] rounded-2xl shadow-sm overflow-hidden">
          {CERT_VERTICALS.map((vertical) => {
            const conf = VERTICAL_CONFIG[vertical];
            const latest = latestByVertical[vertical];
            const isCertified = latest?.passed === true;
            const isInProgress = latest && !latest.passed;
            const isLocked = !latest;
            const certDate = latest?.completedAt ? new Date(latest.completedAt).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : null;
            const pct = isInProgress ? Math.round((latest.score / latest.totalQuestions) * 100) : null;

            return (
              <div key={vertical} className="flex items-center gap-4 px-5 py-4 transition-colors hover:bg-[#FBF7F0] border-b border-[#F0E8DE]">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: conf.dotColor }} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#2C2723] text-sm">{vertical}</div>
                  <div className="text-xs text-[#9C9285] mt-0.5">
                    {isCertified && certDate ? `Certified ${certDate}` :
                     isInProgress && pct !== null ? `In progress — ${pct}%` :
                     "Not started"}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {isCertified ? (
                    <>
                      <button onClick={() => startQuiz(vertical)} className="text-xs font-semibold text-[#E2701F] hover:text-[#C75E12] transition-colors flex items-center gap-1">
                        View badge <ChevronRight className="w-3 h-3" />
                      </button>
                      <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#EFF4E2] text-[#5F8A2E]">Certified</span>
                    </>
                  ) : isInProgress ? (
                    <>
                      <button onClick={() => startQuiz(vertical)} className="text-xs font-semibold text-[#E2701F] hover:text-[#C75E12] transition-colors flex items-center gap-1">
                        Resume <ChevronRight className="w-3 h-3" />
                      </button>
                      <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#FBEEE1] text-[#C75E12]">In progress</span>
                    </>
                  ) : (
                    <>
                      <button onClick={() => startQuiz(vertical)} className="text-xs font-semibold text-[#E2701F] hover:text-[#C75E12] transition-colors flex items-center gap-1">
                        Start <ChevronRight className="w-3 h-3" />
                      </button>
                      <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#F4EEE3] text-[#9C9285]">Locked</span>
                    </>
                  )}
                </div>
              </div>
            );
          })}

          {/* Legacy360 125 Plan — separate cert system (two videos + its own
              quiz), same list, same row pattern as the four verticals above. */}
          {(() => {
            const plan125 = plan125StatusQuery.data;
            const isCertified = plan125?.passed === true;
            const isInProgress = !!plan125 && !plan125.passed && (!!plan125.video1WatchedAt || !!plan125.video2WatchedAt || plan125.attemptNumber > 0);
            return (
              <div className="flex items-center gap-4 px-5 py-4 transition-colors hover:bg-[#FBF7F0]">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: VERTICAL_CONFIG.Legacy360.dotColor }} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#2C2723] text-sm">Legacy360 125 Plan Certification</div>
                  <div className="text-xs text-[#9C9285] mt-0.5">
                    {isCertified ? "Certified" : isInProgress ? "In progress" : "Not started"}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {isCertified ? (
                    <>
                      <button onClick={() => setShowLegacy360Plan125(true)} className="text-xs font-semibold text-[#E2701F] hover:text-[#C75E12] transition-colors flex items-center gap-1">
                        View <ChevronRight className="w-3 h-3" />
                      </button>
                      <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#EFF4E2] text-[#5F8A2E]">Certified</span>
                    </>
                  ) : isInProgress ? (
                    <>
                      <button onClick={() => setShowLegacy360Plan125(true)} className="text-xs font-semibold text-[#E2701F] hover:text-[#C75E12] transition-colors flex items-center gap-1">
                        Resume <ChevronRight className="w-3 h-3" />
                      </button>
                      <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#FBEEE1] text-[#C75E12]">In progress</span>
                    </>
                  ) : (
                    <>
                      <button onClick={() => setShowLegacy360Plan125(true)} className="text-xs font-semibold text-[#E2701F] hover:text-[#C75E12] transition-colors flex items-center gap-1">
                        Start <ChevronRight className="w-3 h-3" />
                      </button>
                      <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#F4EEE3] text-[#9C9285]">Locked</span>
                    </>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
